import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<MultiPartySignatureChipGroup />` — Phase 5 §C-ii scaffold primitive
 * for multi-party signed audit record chip group visualization at audit
 * chain viewer composition seam.
 *
 * Per DD Phase 5 §C §1.3-§1.5 visual treatment specification canonical
 * preservation forward + ADR-022 multi-party signing canonical + SOL
 * Phase 5 §C security posture sweep composition seam canonical
 * preservation forward.
 *
 * # Chip group composition canonical preservation forward
 *
 *   - Horizontal sequence of per-party signature chips (one chip per
 *     signing party); 4px gap between chips
 *   - Connector hairline between adjacent chips (8px-wide at surface-300)
 *     visualizes cryptographic chain composition
 *   - Trailing cryptographic chain integrity indicator badge: ✓ verified /
 *     ✗ tampered / ⟳ pending (Lucide-aligned inline SVG)
 *
 * # Party-anchor color canonical preservation forward (DD §1.4)
 *
 *   - SHN PBC + Smart Health Council: surface-100 bg + brand-anchor fg
 *     (anchored to brand at substrate operates + Council governs)
 *   - Smart Health Trust ("in formation"): surface-50 bg + surface-700 fg
 *     (quieter institutional weight at Trust holds-mediates-advocates)
 *   - Provider + Payer: surface-50 bg + surface-800 fg (institutional
 *     participant; neutral institutional register; NOT party-branded
 *     colors per substrate brand canonical preservation forward)
 *
 * # v0 scaffold scope-fence canonical preservation forward
 *
 * Presentational primitive only — caller supplies signing parties array +
 * integrity verification status. Substantive substrate-side multi-signer
 * data integration (`MultiPartySignerRegistry` + `IPartySigner` per
 * ADR-022) substrate-state-event-paced firing post §C-iii substantive
 * composition cycle canonical preservation forward.
 *
 * # Doctrinal anchors
 *
 *   - NO SHT BACKDOOR — chip group displays metadata only (party identifiers
 *     + signature timestamps); cleartext payload NEVER rendered at
 *     substrate-view per Commitment 6 + Wave 3 §1.2 Step 7
 *   - Storytelling-through-form — chain integrity indicator IS substrate's
 *     structural commitment (audit chain cryptographically attested)
 *     rendered as UI canonical preservation forward
 *   - Substrate-anchor visual ratio — party-anchor colors NOT
 *     party-branded; substrate brand anchors visualization
 */
import * as React from 'react';
const SignatureIcon = () => (_jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M20 19c-2.79.97-6.16-.43-7.91-2.93-.57-.82-1.61-1.07-2.39-.55l-.86.58c-.78.53-1.83.23-2.39-.55C4.85 12.79 5.42 9.45 7.5 7.5c2.79-2.79 7.21-2.79 10 0" }), _jsx("path", { d: "M14 16c.5 0 1-.5 1-1 0-1-1-2-2-2-1.5 0-2 1.5-2 3 0 2 1.5 4 4 4 2 0 3-1 3-3" })] }));
const CheckCircleIcon = () => (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "m9 12 2 2 4-4" })] }));
const XCircleIcon = () => (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("path", { d: "m15 9-6 6" }), _jsx("path", { d: "m9 9 6 6" })] }));
const LoaderIcon = () => (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", className: "multi-party-signature-chip-group__pending-spinner", children: _jsx("path", { d: "M21 12a9 9 0 1 1-6.219-8.56" }) }));
function renderIntegrityIcon(state) {
    switch (state) {
        case 'verified':
            return _jsx(CheckCircleIcon, {});
        case 'tampered':
            return _jsx(XCircleIcon, {});
        case 'pending':
            return _jsx(LoaderIcon, {});
    }
}
function integrityTooltip(state) {
    switch (state) {
        case 'verified':
            return 'Cryptographic chain verified';
        case 'tampered':
            return 'Cryptographic chain integrity check failed — escalate to substrate operator';
        case 'pending':
            return 'Verifying cryptographic chain';
    }
}
export function MultiPartySignatureChipGroup(props) {
    const { parties, integrity, testId } = props;
    return (_jsxs("span", { className: "multi-party-signature-chip-group", "data-testid": testId ?? 'multi-party-signature-chip-group', "data-integrity": integrity, role: "group", "aria-label": `Multi-party signed audit record (${parties.length} signing parties; chain integrity: ${integrity})`, children: [parties.map((party, i) => (_jsxs(React.Fragment, { children: [i > 0 && (_jsx("span", { className: "multi-party-signature-chip-group__connector", "aria-hidden": "true" })), _jsxs("span", { className: `multi-party-signature-chip multi-party-signature-chip--${party.kind}`, "data-testid": `multi-party-signature-chip-${party.kind}`, title: party.signatureFingerprint !== undefined && party.signedAt !== undefined
                            ? `${party.displayName} signed at ${party.signedAt}; fingerprint ${party.signatureFingerprint}`
                            : party.displayName, children: [_jsx("span", { className: "multi-party-signature-chip__icon", "aria-hidden": "true", children: _jsx(SignatureIcon, {}) }), _jsx("span", { className: "multi-party-signature-chip__label", children: party.displayName })] })] }, `${party.kind}-${i}`))), _jsx("span", { className: `multi-party-signature-chip-group__integrity multi-party-signature-chip-group__integrity--${integrity}`, "data-testid": `chain-integrity-${integrity}`, title: integrityTooltip(integrity), role: "status", children: renderIntegrityIcon(integrity) })] }));
}
//# sourceMappingURL=multi-party-signature-chip-group.js.map